import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';
import { ChevronRight } from 'lucide-react';

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(localStorage.getItem('betti_sort_by') || 'relevance');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const cat = (await api.get(`/categories/${slug}`)).data;
        if (cancelled) return;
        setCategory(cat);
        // Get all categories to compute breadcrumb and subcategories
        const allCats = (await api.get('/categories')).data;
        if (cancelled) return;
        const subs = allCats.filter((c) => c.parent_id === cat.id);
        setSubcategories(subs);
        // Build breadcrumb up the tree
        const trail = [cat];
        let cur = cat;
        while (cur.parent_id) {
          const p = allCats.find((c) => c.id === cur.parent_id);
          if (!p) break;
          trail.unshift(p);
          cur = p;
        }
        setBreadcrumb(trail);
        // Fetch only products directly in this category (server-side filter, avoids downloading whole catalog)
        const prods = (await api.get(`/products?category=${encodeURIComponent(slug)}`)).data;
        if (cancelled) return;
        setProducts(prods);
      } catch (e) {
        if (!cancelled) setCategory(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const sortedProducts = useMemo(() => {
    const arr = [...products];
    if (sortBy === 'alphabetical') arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'));
    else if (sortBy === 'alphabetical-desc') arr.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'it'));
    else if (sortBy === 'price-asc') arr.sort((a, b) => (a.price_per_day || 0) - (b.price_per_day || 0));
    else if (sortBy === 'price-desc') arr.sort((a, b) => (b.price_per_day || 0) - (a.price_per_day || 0));
    return arr;
  }, [products, sortBy]);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <nav className="flex items-center text-sm text-neutral-500 mb-4 flex-wrap">
        <Link to="/" className="hover:text-[#d62828]">Home</Link>
        <ChevronRight size={14} className="mx-2" />
        <Link to="/noleggio" className="hover:text-[#d62828]">Noleggio</Link>
        {breadcrumb.slice(0, -1).map((b) => (
          <React.Fragment key={b.id}>
            <ChevronRight size={14} className="mx-2" />
            <Link to={`/categoria/${b.slug}`} className="hover:text-[#d62828]">{b.name}</Link>
          </React.Fragment>
        ))}
        {category && (
          <>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-neutral-800 font-medium">{category.name}</span>
          </>
        )}
      </nav>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <h1 className="text-4xl font-black brand-text text-neutral-900">{category?.name || 'Categoria'}</h1>
          {category?.description && <p className="text-neutral-600 mt-2 max-w-2xl">{category.description}</p>}
        </div>
        <p className="text-sm text-neutral-500 mt-3 md:mt-0">{products.length} prodotti</p>
      </div>

      {/* Subcategories grid */}
      {subcategories.length > 0 && (
        <section className="mb-10">
          <h2 className="text-lg font-bold text-neutral-700 mb-3">Sottocategorie</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {subcategories.map((s) => (
              <Link
                key={s.id}
                to={`/categoria/${s.slug}`}
                className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-neutral-200 hover:border-[#d62828]/50 hover:shadow-md transition-all"
              >
                {s.image_url ? (
                  <img src={s.image_url} alt={s.name} className="absolute inset-0 w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 bg-neutral-100" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                  <h3 className="font-bold text-sm leading-tight">{s.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Products */}
      {loading ? (
        <div className="text-neutral-500">Caricamento...</div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <p>Non ci sono ancora prodotti in questa categoria.</p>
          <Link to="/noleggio" className="inline-block mt-4 text-[#d62828] font-bold">Vai al catalogo</Link>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <p className="text-sm text-neutral-500">{products.length} prodotti</p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-neutral-600 font-medium">Ordina per:</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  localStorage.setItem('betti_sort_by', e.target.value);
                }}
                className="px-3 py-1.5 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
                data-testid="sort-select"
              >
                <option value="relevance">Rilevanza (default)</option>
                <option value="alphabetical">Nome A → Z</option>
                <option value="alphabetical-desc">Nome Z → A</option>
                <option value="price-asc">Prezzo crescente</option>
                <option value="price-desc">Prezzo decrescente</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sortedProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryPage;
